import { useEffect, useState } from 'react'
import {
  archiveAgentJobDescription,
  fetchAgentJobDescriptions,
  updateAgentJobDescription,
} from '../../api/ws'
import { useAppStore } from '../../store/app'
import type { AgentJobDescription } from '../../store/types'

// Temporary CRUD view for agent job descriptions in the caller's
// current org. Mirrors WorkingDirectoriesSheet: inline rename of the
// title + Archive button with cascade fallback.
//
// Inline edit is title only here — mandate / required_skills are
// long-form text and need a richer editor (out of scope for a debug
// surface). Title rename goes through update_agent_job_description
// and may collide with a sibling row's title; server returns the
// raw error and we surface it.
//
// Reachable via gear → About → Debug → "Job Descriptions".
export function AgentJobDescriptionsSheet() {
  const jds = useAppStore((s) => s.agentJobDescriptions)
  const orgs = useAppStore((s) => s.availableOrganizations)
  const currentOrg = orgs.find((o) => o.is_current)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (currentOrg?.id) fetchAgentJobDescriptions(currentOrg.id)
  }, [currentOrg?.id])

  function handleError(e: unknown) {
    setError(typeof e === 'string' ? e : (e as Error)?.message ?? String(e))
  }

  function refresh() {
    if (currentOrg?.id) fetchAgentJobDescriptions(currentOrg.id)
  }

  if (!currentOrg) {
    return (
      <div style={{ padding: '12px 16px', fontSize: 14 }}>
        <p style={{ opacity: 0.6 }}>No current organization.</p>
      </div>
    )
  }

  // Hide archived and temp (title === 'Temp') rows.
  const live = jds.filter((j) => !j.archived_at && j.title !== 'Temp')

  return (
    <div style={{ padding: '12px 16px', fontSize: 14 }}>
      {error && (
        <div
          style={{
            padding: '8px 12px',
            background: '#fee',
            color: '#900',
            borderRadius: 4,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      {live.length === 0 ? (
        <p style={{ opacity: 0.6 }}>No job descriptions in this organization.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {live.map((j) => (
            <JDRow
              key={j.id}
              jd={j}
              onError={handleError}
              onClearError={() => setError(null)}
              onMutated={refresh}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function JDRow({
  jd,
  onError,
  onClearError,
  onMutated,
}: {
  jd: AgentJobDescription
  onError: (e: unknown) => void
  onClearError: () => void
  onMutated: () => void
}) {
  const [titleDraft, setTitleDraft] = useState(jd.title)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    setTitleDraft(jd.title)
  }, [jd.title])

  const titleDirty = titleDraft.trim() !== jd.title

  async function saveTitle() {
    if (!titleDirty) return
    onClearError()
    setBusy(true)
    try {
      const resp = await updateAgentJobDescription({
        id: jd.id,
        title: titleDraft.trim(),
        mandate: jd.mandate,
        requiredSkills: jd.required_skills,
        priority: jd.priority,
      })
      if (resp?.error) {
        onError(resp.error)
        return
      }
      onMutated()
    } catch (e) {
      onError(e)
    } finally {
      setBusy(false)
    }
  }

  async function handleArchive() {
    if (!confirm(`Archive job description "${jd.title}"?`)) return
    onClearError()
    setBusy(true)
    try {
      let resp = await archiveAgentJobDescription({ id: jd.id })
      if (resp?.error === 'has_live_dependents') {
        const positions = resp.blockers?.positions ?? []
        const agentCount = positions.reduce(
          (n, p) => n + (p.agents?.length ?? 0),
          0,
        )
        const lines = positions.map(
          (p) =>
            `• position ${p.name}` +
            (p.agents?.length ? ` (${p.agents.length} agent(s))` : ''),
        ).join('\n')
        const ok = confirm(
          `This JD is held by ${positions.length} live position(s) ` +
            `and ${agentCount} agent(s):\n${lines}\n\n` +
            `Retire the agents, eliminate the positions, and archive the JD?`,
        )
        if (!ok) {
          setBusy(false)
          return
        }
        resp = await archiveAgentJobDescription({ id: jd.id, cascade: true })
      }
      if (resp?.error) {
        onError(resp.error)
        return
      }
      onMutated()
    } catch (e) {
      onError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <li
      style={{
        padding: '12px 0',
        borderBottom: '1px solid #eee',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <div style={{ fontWeight: 600 }}>{jd.title}</div>
        <div style={{ opacity: 0.6, fontSize: 12 }}>priority {jd.priority}</div>
      </div>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 12, opacity: 0.7 }}>Rename</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            style={{ flex: 1, fontSize: 16 }}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <button onClick={saveTitle} disabled={busy || !titleDirty}>
            Save
          </button>
        </div>
      </label>

      {jd.mandate && (
        <div style={{ opacity: 0.7, fontSize: 12, whiteSpace: 'pre-wrap' }}>
          {jd.mandate.length > 200 ? jd.mandate.slice(0, 200) + '…' : jd.mandate}
        </div>
      )}

      <div>
        <button
          onClick={handleArchive}
          disabled={busy}
          style={{ color: '#900' }}
        >
          Archive
        </button>
      </div>
    </li>
  )
}
